import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MessageCircle, CheckCircle, Shield, Headphones, Sparkles } from "lucide-react";

export function HeroSection() {
  return (
    <section id="home" className="relative overflow-hidden gradient-hero py-16 lg:py-24">
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-20 h-20 bg-primary/10 rounded-full blur-xl animate-float" />
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-accent/10 rounded-full blur-xl animate-float" style={{ animationDelay: "1s" }} />
        <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-secondary/10 rounded-full blur-xl animate-float" style={{ animationDelay: "2s" }} />
      </div>

      <div className="container relative">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6 animate-fade-in">
            <p className="text-sm tracking-widest uppercase text-muted-foreground font-medium">
              Suppliers Aplikasi Premium
            </p>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight">
              Marketplace{" "}
              <span className="text-gradient">Aplikasi Premium</span>
              <br />
              Terpercaya & Terjangkau
            </h1>

            <p className="text-lg text-muted-foreground max-w-xl">
              Spesialis penjualan akun premium Netflix, Spotify, CapCut, ChatGPT dan aplikasi premium lainnya. Ready stock langsung dari tim internal, aman dan siap digunakan.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link to="/products">
                <Button size="lg" className="gradient-primary text-primary-foreground border-0 gap-2 shadow-lg hover:shadow-glow transition-shadow">
                  Lihat Semua Aplikasi
                </Button>
              </Link>
              <a href="https://wa.me/6281219774450">
                <Button size="lg" variant="outline" className="gap-2 border-primary/30 hover:bg-blue-light">
                  <MessageCircle className="h-5 w-5" />
                  Chat Admin
                </Button>
              </a>
            </div>

            <div className="flex flex-wrap gap-6 pt-4">
              <div className="flex items-center gap-2 text-sm">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-success/10">
                  <CheckCircle className="h-4 w-4 text-success" />
                </div>
                <span className="font-medium">100% Premium Features</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                  <Shield className="h-4 w-4 text-primary" />
                </div>
                <span className="font-medium">Garansi Aman</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-info/10">
                  <Headphones className="h-4 w-4 text-info" />
                </div>
                <span className="font-medium">Full Support</span>
              </div>
            </div>
          </div>

          <div className="relative animate-slide-in-right" style={{ animationDelay: "0.2s" }}>
            <div className="relative w-full max-w-xl mx-auto">
              {/* Floating badges */}
              <div className="absolute -top-2 left-4 z-20 animate-bounce-slow">
                <Badge className="bg-destructive text-destructive-foreground border-0 px-3 py-1 text-xs font-semibold shadow-lg">
                  🔥 Hot
                </Badge>
              </div>
              <div className="absolute top-12 -right-2 z-20 animate-bounce-slow" style={{ animationDelay: "0.5s" }}>
                <Badge className="bg-success text-success-foreground border-0 px-3 py-1 text-xs font-semibold shadow-lg">
                  ✨ New
                </Badge>
              </div>
              <div className="absolute bottom-8 left-0 z-20 animate-bounce-slow" style={{ animationDelay: "1s" }}>
                <Badge className="bg-warning text-warning-foreground border-0 px-3 py-1 text-xs font-semibold shadow-lg">
                  🎉 Promo
                </Badge>
              </div>

              {/* App cards showcase - Vertical scrolling marquee */}
              <div className="relative h-[380px] overflow-hidden rounded-2xl">
                {/* Gradient overlays for smooth fade effect */}
                <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-background to-transparent z-10 pointer-events-none" />
                <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background to-transparent z-10 pointer-events-none" />
                
                <div className="flex flex-col gap-4 animate-marquee-vertical">
                  {[...Array(2)].map((_, idx) => (
                    <div key={idx} className="flex flex-col gap-4">
                      <div className="flex gap-4">
                        <div className="p-4 bg-card rounded-xl shadow-card hover:shadow-lg transition-all hover:scale-105 flex-1">
                          <div className="h-20 rounded-lg bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center">
                            <span className="text-2xl font-bold text-white">N</span>
                          </div>
                          <p className="mt-2 text-sm font-medium text-center">Netflix</p>
                        </div>
                        <div className="p-4 bg-card rounded-xl shadow-card hover:shadow-lg transition-all hover:scale-105 flex-1">
                          <div className="h-20 rounded-lg bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center">
                            <span className="text-2xl">🎵</span>
                          </div>
                          <p className="mt-2 text-sm font-medium text-center">Spotify</p>
                        </div>
                        <div className="p-4 bg-card rounded-xl shadow-card hover:shadow-lg transition-all hover:scale-105 flex-1">
                          <div className="h-20 rounded-lg bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center">
                            <span className="text-2xl">▶️</span>
                          </div>
                          <p className="mt-2 text-sm font-medium text-center">YouTube</p>
                        </div>
                      </div>
                      <div className="flex gap-4">
                        <div className="p-4 bg-card rounded-xl shadow-card hover:shadow-lg transition-all hover:scale-105 flex-1">
                          <div className="h-20 rounded-lg bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center">
                            <span className="text-2xl">🤖</span>
                          </div>
                          <p className="mt-2 text-sm font-medium text-center">ChatGPT</p>
                        </div>
                        <div className="p-4 bg-card rounded-xl shadow-card hover:shadow-lg transition-all hover:scale-105 flex-1">
                          <div className="h-20 rounded-lg bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center">
                            <span className="text-2xl">✂️</span>
                          </div>
                          <p className="mt-2 text-sm font-medium text-center">CapCut</p>
                        </div>
                        <div className="p-4 bg-card rounded-xl shadow-card hover:shadow-lg transition-all hover:scale-105 flex-1">
                          <div className="h-20 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
                            <span className="text-2xl">📘</span>
                          </div>
                          <p className="mt-2 text-sm font-medium text-center">Canva</p>
                        </div>
                      </div>
                      <div className="flex gap-4">
                        <div className="p-4 bg-card rounded-xl shadow-card hover:shadow-lg transition-all hover:scale-105 flex-1">
                          <div className="h-20 rounded-lg bg-gradient-to-br from-sky-400 to-sky-600 flex items-center justify-center">
                            <span className="text-2xl">☁️</span>
                          </div>
                          <p className="mt-2 text-sm font-medium text-center">iCloud</p>
                        </div>
                        <div className="p-4 bg-card rounded-xl shadow-card hover:shadow-lg transition-all hover:scale-105 flex-1">
                          <div className="h-20 rounded-lg bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center">
                            <span className="text-2xl">🎮</span>
                          </div>
                          <p className="mt-2 text-sm font-medium text-center">VidIO</p>
                        </div>
                        <div className="p-4 bg-card rounded-xl shadow-card hover:shadow-lg transition-all hover:scale-105 flex-1">
                          <div className="h-20 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center">
                            <span className="text-2xl">🎬</span>
                          </div>
                          <p className="mt-2 text-sm font-medium text-center">Disney+</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Guarantee badge */}
              <div className="mt-4 flex items-center justify-center gap-2 bg-success/10 rounded-full px-4 py-2">
                <Shield className="h-5 w-5 text-success" />
                <span className="text-sm font-semibold text-success">Garansi 100% aman dan nyaman!</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
